import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Rating,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  LocationOn as LocationIcon, 
  ContentCut as SalonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const FeaturedSalons = ({ featuredSalons, scrollToSearch }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [translateX, setTranslateX] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Configuration du carrousel infini
  const cardWidth = 280; // Largeur fixe des cartes en pixels
  const gap = 24; // Espacement entre les cartes
  const cardWithGap = cardWidth + gap;
  
  // Créer un tableau étendu pour l'effet infini
  const extendedSalons = featuredSalons ? [
    ...featuredSalons,
    ...featuredSalons,
    ...featuredSalons
  ] : [];

  // Auto-play du carrousel infini
  useEffect(() => {
    if (!featuredSalons || featuredSalons.length === 0) return;

    const interval = setInterval(() => {
      setTranslateX(prev => {
        const newTranslateX = prev - cardWithGap;
        
        // Reset quand on atteint la fin du premier set
        if (Math.abs(newTranslateX) >= cardWithGap * featuredSalons.length) {
          setIsTransitioning(false);
          setTimeout(() => {
            setIsTransitioning(true);
          }, 50);
          return 0;
        }
        
        return newTranslateX;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [featuredSalons, cardWithGap]);

  if (!featuredSalons || featuredSalons.length === 0) {
    return (
      <Container id="featured-salons" maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#212121',
              mb: 2,
              fontFamily: '"Inter", sans-serif'
            }}
          >
            {t('featured.partnersInSpotlight')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ 
              maxWidth: 600, 
              mx: 'auto',
              fontFamily: '"Inter", sans-serif'
            }}
          >
            {t('featured.noSalonsAvailable')}
          </Typography>
        </Box>
      </Container>
    );
  }

  const SalonCard = ({ salon }) => {
    const hasPhotos = salon.photos && salon.photos.length > 0 && salon.photos[0] && salon.photos[0].trim() !== '';
    
    return (
      <Card
        sx={{
          height: 350,
          width: '100%',
          cursor: 'pointer',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid rgba(0,0,0,0.04)',
          '&:hover': {
            transform: 'translateY(-12px) scale(1.02)',
          }
        }}
        onClick={() => navigate(`/salon/${salon.id}`)}
      >
        <Box sx={{ position: 'relative', height: '65%' }}>
          {hasPhotos ? (
            <>
              <CardMedia
                component="img"
                height="100%"
                image={salon.photos[0]}
                alt={salon.salonName}
                className="salon-image"
                sx={{
                  objectFit: 'cover',
                  transition: 'transform 0.4s ease',
                }}
              />
              {/* Overlay gradient */}
              <Box
                className="salon-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}
              />
              {/* Badge premium */}
              {salon.featured && (
                <Chip
                  label={`⭐ ${t('featured.featured')}`}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    color: '#212121',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    fontFamily: '"Inter", sans-serif'
                  }}
                />
              )}
            </>
          ) : (
            <Box
              className="salon-image"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
                transition: 'transform 0.4s ease',
                background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8e8 100%)',
              }}
            >
              <SalonIcon
                sx={{
                  fontSize: 56,
                  color: '#bbb',
                  mb: 1
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontWeight: 500,
                  textAlign: 'center',
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                {t('featured.photoComingSoon')}
              </Typography>
            </Box>
          )}
        </Box>
      
        <CardContent sx={{ p: 3, height: '35%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 700,
                color: '#212121',
                mb: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: '"Inter", sans-serif',
                fontSize: '1.1rem'
              }}
            >
              {salon.salonName}
            </Typography>
            
            <Box display="flex" alignItems="center" mb={2}>
              <LocationIcon fontSize="small" sx={{ mr: 0.5, color: '#888' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 500
                }}
              >
                {salon.city}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {/* Afficher les avis pour tous les salons mockup */}
            {salon.rating ? (
              <Box display="flex" alignItems="center">
                <Rating
                  value={salon.rating || 0}
                  readOnly
                  size="small"
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: '#ffc107',
                    }
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    ml: 0.5, 
                    color: '#666',
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 500
                  }}
                >
                  ({salon.reviewCount || 0})
                </Typography>
              </Box>
            ) : (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#999', 
                  fontStyle: 'italic',
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                {t('featured.newSalon')}
              </Typography>
            )}
            
            <Chip
              label={salon.category.split(' ')[0]} // Raccourcir le texte
              size="small"
              sx={{
                backgroundColor: 'rgba(245, 245, 220, 0.8)',
                color: '#212121',
                fontSize: '0.7rem',
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
                borderRadius: 2,
                border: '1px solid rgba(245, 245, 220, 0.5)'
              }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container id="featured-salons" maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography
          variant="h2"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#212121',
            mb: 2
          }}
        >
          {t('featured.partnersInSpotlight')}
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            maxWidth: 600,
            mx: 'auto',
            fontWeight: 400
          }}
        >
          {t('featured.discoverProfessionals')}
        </Typography>
      </Box>

      {/* Carrousel infini automatique */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            transform: `translateX(${translateX}px)`,
            transition: isTransitioning ? 'transform 0.8s ease-in-out' : 'none',
            gap: `${gap}px`,
            width: 'fit-content'
          }}
        >
          {extendedSalons.map((salon, index) => (
            <Box
              key={`${salon.id}-${index}`}
              sx={{
                width: `${cardWidth}px`,
                flexShrink: 0
              }}
            >
              <SalonCard salon={salon} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box textAlign="center" mt={6}>
        <Button
          variant="outlined"
          size="large"
          sx={{
            borderColor: '#212121',
            color: '#212121',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 3,
            textTransform: 'none',
            fontFamily: '"Inter", sans-serif',
            '&:hover': {
              backgroundColor: '#212121',
              color: 'white',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(33, 33, 33, 0.15)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('featured.viewAllSalons')}
        </Button>
      </Box>
    </Container>
  );
};

export default FeaturedSalons;